import { money, evaluatePricing, applyPricingReview, quoteDecisionPermission } from "../../models/price-drawer/quotePricingPolicy";
import { fetchCostPrices } from "../../models/shared/costPricesModel";
import { useAccess } from "../../../../features/access/context/AccessProvider";
import React, { useState, useEffect, useMemo } from "react";
import {
  Modal, Button, Checkbox, InputNumber, Select, Input, Alert, Tabs, Spin, Tag, Avatar, Tooltip,
  message as antMessage,
} from "antd";
import {
  SendOutlined, CheckCircleOutlined, CloseCircleOutlined,
  ShopOutlined, UserOutlined, QuestionCircleOutlined, WarningOutlined, ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import { substituteQRText, loadConditionLabels, useQv4Theme } from "../../helpers";
import { gpFromSp, QR_IDS, injectPartsTemplate } from "./PriceUpdateDrawer";
import { OverviewTab } from "../workflow-drawer/OverviewTab";
import { fetchQuickReplies } from "../../models/chat/chatUtilityModel";
import { fetchTaskDetail } from "../../models/shared/taskDetailModel";
import { fetchSrmUsers } from "../../models/shared/agentsUsersModel";
import { approveApproval, rejectApproval } from "../../models/board/queriesModel";

dayjs.extend(relativeTime);
dayjs.extend(utc);

export function QuoteApprovalModal({ open, onClose, approval, authState, userId, onDone, effectivePriority = null }) {
  const { can, refreshAccess } = useAccess();
  const canDecide = can("approvals.decide") && can(quoteDecisionPermission(approval?.action_key)) && effectivePriority !== null && effectivePriority >= (approval?.required_priority ?? 0);
  const { t, isDark } = useQv4Theme();
  const payload = approval?.modified_payload ?? approval?.payload ?? {};
  const belowGpAction = approval?.action_key === "quote_send_below_recommended_gp";
  const [costPrices, setCostPrices] = useState([]);
  const [policyLoading, setPolicyLoading] = useState(true);
  const [reviewCorrection, setReviewCorrection] = useState(null);
  const [pricingNotice, setPricingNotice] = useState("");
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);

  const [partSp, setPartSp] = useState({});
  const [partVat, setPartVat] = useState({});
  const [partOldExchange, setPartOldExchange] = useState({});
  const [deliveryAmount, setDeliveryAmount] = useState(0);

  const [includedPartIds, setIncludedPartIds] = useState(new Set());
  const [activeTab, setActiveTab] = useState("b2c");
  const [messageText, setMessageText] = useState("");
  const [keepAgentText, setKeepAgentText] = useState(true);
  const [textManuallyEdited, setTextManuallyEdited] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const [qrLoading, setQrLoading] = useState(false);
  // Bumped when the condition code→label map arrives (old payloads store numeric codes)
  const [condVersion, setCondVersion] = useState(0);
  const [fullTask, setFullTask] = useState(null);
  const [fullTaskLoading, setFullTaskLoading] = useState(false);
  const [userMap, setUserMap] = useState({});

  const isCustomised = !!payload.message_customised;
  const rawType = payload.customer_type ?? null;
  const isB2B = rawType === 0 || rawType === "0";
  const isB2C = rawType === 1 || rawType === "1";

  useEffect(() => {
    if (!open || !payload.parts) return;
    loadConditionLabels().then(() => setCondVersion(v => v + 1));
    const sp = {};
    const vat = {};
    const oldExchange = {};
    const included = new Set();
    (payload.parts ?? []).forEach(p => {
      sp[p.query_part_id] = p.base_sp ?? p.sp;
      // vat_mode is the current field; fall back to the old boolean for
      // approval requests submitted before the mode dropdown existed.
      vat[p.query_part_id] = { pct: p.vat_pct ?? 5, mode: p.vat_mode ?? (p.vat_enabled ? "incl" : "none") };
      oldExchange[p.query_part_id] = !!p.old_exchange;
      if (p.included) included.add(p.query_part_id);
    });
    setPartSp(sp);
    setPartVat(vat);
    setPartOldExchange(oldExchange);
    setIncludedPartIds(included);
    setDeliveryAmount(payload.delivery_amount ?? 0);
    setDeliveryEnabled(payload.delivery_enabled ?? Number(payload.delivery_amount || 0) > 0);
    setReviewCorrection(null); setPricingNotice("");
    setActiveTab(payload.template_type ?? "b2c");
    setMessageText(payload.message_text ?? "");
    setKeepAgentText(true);
    setTextManuallyEdited(false);
    setRejectMode(false);
    setRejectReason("");
    setApproving(false);
    setRejecting(false);
    setIsDirty(false);
    if (quickReplies.length) return;
    setQrLoading(true);
    fetchQuickReplies(userId).then(list => {
      setQuickReplies(list.filter(qr => !qr.type || qr.type === "text"));
      setQrLoading(false);
    });
  }, [open]); // eslint-disable-line

  // Full query context (vehicle, customer, timeline) for the admin to
  // reference before approving — this isn't part of the approval payload
  // itself, so it's fetched separately from the same endpoint the task
  // drawer uses.
  useEffect(() => {
    if (!open || (!payload.query_pk && !payload.query_id)) { setFullTask(null); return; }
    setFullTaskLoading(true);
    fetchTaskDetail(payload.query_pk ?? payload.query_id)
      .then(result => { if (result) setFullTask(result); })
      .catch(() => setFullTask(null))
      .finally(() => setFullTaskLoading(false));
  }, [open, payload.query_pk, payload.query_id]); // eslint-disable-line

  useEffect(() => {
    if (!open) return;
    fetchSrmUsers().then(list => {
      const map = {};
      list.forEach(u => { map[u.id] = u.fullName; });
      setUserMap(map);
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPolicyLoading(true);
    fetchCostPrices({ force: true }).then(rows => {
      if (!cancelled) { setCostPrices(rows); setPolicyLoading(false); }
    });
    return () => { cancelled = true; };
  }, [open]);

  const pricingInputs = (payload.parts || []).map(p => ({ ...p,
    base_sp: partSp[p.query_part_id] ?? p.base_sp ?? p.sp,
    included: includedPartIds.has(p.query_part_id) }));
  const pricingKey = JSON.stringify(pricingInputs.map(p => [p.query_part_id, p.cp, p.base_sp, p.included]));
  const originalKey = JSON.stringify((payload.parts || []).map(p => [p.query_part_id, p.cp, p.base_sp ?? p.sp, !!p.included]));
  const localReview = evaluatePricing(pricingInputs, costPrices, fullTask?.task?.timeline);
  const authoritativeReview = reviewCorrection?.key === pricingKey ? reviewCorrection.review
    : pricingKey === originalKey ? payload.pricing_review : null;
  const pricingReview = authoritativeReview ? { ...authoritativeReview, parts: applyPricingReview(localReview.parts, authoritativeReview) } : localReview;
  const pricedById = new Map(pricingReview.parts.map(p => [Number(p.query_part_id), p]));
  const pricingVersionKey = JSON.stringify(pricingReview.parts);
  const needsDedicatedCycle = !belowGpAction && pricingReview.below_recommended_gp;

  const communicationChannel = fullTask?.task?.customer_communication_channel || null;
  const manualChannel = communicationChannel?.key && communicationChannel.key !== "wati";

  const syntheticCard = useMemo(() => ({
    query_id: payload.query_id,
    customer_name: payload.customer_name,
    customer: { customer_name: payload.customer_name },
  }), [payload.query_id, payload.customer_name]);

  const selectedPartsForInjection = useMemo(() =>
    (payload.parts ?? [])
      .filter(p => includedPartIds.has(p.query_part_id))
      .map(p => ({
        part: { part_name: p.part_name, condition_preference: p.condition },
        quote: { warranty_days: p.warranty_days, quoted_price: p.cp },
        sp: pricedById.get(Number(p.query_part_id))?.sp ?? p.sp,
        vatPct: partVat[p.query_part_id]?.pct ?? p.vat_pct ?? 5,
        vatMode: partVat[p.query_part_id]?.mode ?? p.vat_mode ?? (p.vat_enabled ? "incl" : "none"),
        oldExchange: partOldExchange[p.query_part_id] ?? !!p.old_exchange,
      })),
    [payload.parts, includedPartIds, partSp, partVat, partOldExchange, pricingVersionKey] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const deliveryPerPart = selectedPartsForInjection.length > 0
    ? money((deliveryEnabled ? deliveryAmount || 0 : 0) / selectedPartsForInjection.length)
    : 0;

  const getTemplate = (tab) => {
    const id = tab === "b2b" ? QR_IDS.b2b : QR_IDS.b2c;
    return quickReplies.find(qr => qr.id === id || qr._id === id);
  };

  // Re-inject template text when values change, unless keeping agent's text or admin edited directly
  useEffect(() => {
    if (!open) return;
    if (isCustomised && keepAgentText) return;
    if (textManuallyEdited) return;
    const qr = getTemplate(activeTab);
    if (!qr || quickReplies.length === 0) return;
    const base = substituteQRText(qr.text || "", authState);
    setMessageText(injectPartsTemplate(base, syntheticCard, selectedPartsForInjection, deliveryPerPart));
  }, [open, activeTab, selectedPartsForInjection, quickReplies, deliveryPerPart, keepAgentText, condVersion]); // eslint-disable-line

  const markDirty = () => setIsDirty(true);

  const togglePart = (qpId) => {
    setIncludedPartIds(prev => {
      const next = new Set(prev);
      if (next.has(qpId)) next.delete(qpId); else next.add(qpId);
      return next;
    });
    markDirty();
  };

  const updateVat = (qpId, key, val) => {
    setPartVat(prev => ({ ...prev, [qpId]: { ...(prev[qpId] ?? { pct: 5, mode: "excl" }), [key]: val } }));
    markDirty();
  };

  const toggleOldExchange = (qpId) => {
    setPartOldExchange(prev => ({ ...prev, [qpId]: !prev[qpId] }));
    markDirty();
  };

  const buildModifiedPayload = () => {
    const parts = (payload.parts ?? []).map(p => {
      const isIncluded = includedPartIds.has(p.query_part_id);
      const calculated = pricedById.get(Number(p.query_part_id));
      const sp = calculated.sp;
      const { pct = 5, mode = "excl" } = partVat[p.query_part_id] ?? {};
      const share = isIncluded ? deliveryPerPart : 0;
      const finalPrice = money(mode === "incl" ? (sp + share) * (1 + pct / 100) : (sp + share));
      return {
        ...p,
        included: isIncluded,
        sp,
        base_sp: calculated.base_sp,
        cp: calculated.cp,
        vat_pct: pct,
        vat_enabled: mode !== "none", // kept for older readers of this payload shape
        vat_mode: mode,
        delivery_share: isIncluded ? Number(deliveryPerPart.toFixed(2)) : 0,
        final_price: finalPrice,
        old_exchange: partOldExchange[p.query_part_id] ?? !!p.old_exchange,
      };
    });
    return {
      ...payload,
      pricing_policy_version: 2,
      delivery_enabled: deliveryEnabled,
      delivery_amount: deliveryEnabled ? deliveryAmount ?? 0 : 0,
      message_text: messageText.trim(),
      parts,
    };
  };

  const handleApprove = async () => {
    if (!canDecide || needsDedicatedCycle || policyLoading) return;
    if (selectedPartsForInjection.length === 0) {
      antMessage.warning("At least one part must be included before approving");
      return;
    }
    setApproving(true);
    try {
      await approveApproval(approval.id, {
        userId,
        modified_payload: isDirty || payload.pricing_policy_version !== 2 ? buildModifiedPayload() : null,
      });
      antMessage.success(manualChannel ? "Quote approved and recorded as manually shared" : "Quote approved and sent to customer");
      onDone?.("approved", approval.id);
      onClose();
    } catch (e) {
      if (e?.response?.status === 403) refreshAccess?.({ background: true }).catch(() => {});
      const review = e?.response?.data?.data?.pricing_review;
      if (e?.response?.status === 409 && review?.parts) {
        setReviewCorrection({ key: pricingKey, review });
        setPricingNotice("Pricing changed on the server. Review the refreshed figures and message before approving again.");
        setIsDirty(true); setKeepAgentText(false); setTextManuallyEdited(false);
      } else if (e?.response?.status === 409) {
        setPricingNotice(`${e.response.data?.message || "Quote data changed."} Ask the salesperson to reload supplier quotes and submit an updated request.`);
        fetchTaskDetail(payload.query_pk ?? payload.query_id).then(result => { if (result) setFullTask(result); });
      } else {
        antMessage.error(e?.response?.data?.message || "Failed to approve");
      }
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!canDecide) return;
    if (!rejectReason.trim()) {
      antMessage.warning("Please enter a reason for rejection");
      return;
    }
    setRejecting(true);
    try {
      await rejectApproval(approval.id, { userId, reason: rejectReason.trim() });
      antMessage.info("Quote rejected — agent has been notified");
      onDone?.("rejected", approval.id);
      onClose();
    } catch (e) {
      if (e?.response?.status === 403) refreshAccess?.({ background: true }).catch(() => {});
      antMessage.error(e?.response?.data?.message || "Failed to reject");
    } finally {
      setRejecting(false);
    }
  };

  if (!approval) return null;

  const contextCol = (
    <div style={{ overflowY: "auto", paddingRight: 4, borderRight: `1px solid ${t.border}` }}>
      <div style={{ fontWeight: 600, fontSize: 12, color: t.textSub, marginBottom: 8 }}>Query Details</div>
      {fullTaskLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 24 }}><Spin size="small" /></div>
      ) : fullTask?.task ? (
        <OverviewTab task={fullTask.task} afterSales={fullTask.after_sales} parts={fullTask.parts ?? []} userMap={userMap} />
      ) : (
        <div style={{ fontSize: 12, color: t.textSub }}>No additional query details available.</div>
      )}
    </div>
  );

  const leftCol = (
    <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, paddingRight: 4 }}>
      {/* Customer info */}
      <div style={{ padding: "8px 10px", background: t.hover, borderRadius: 6, fontSize: 12, flexShrink: 0 }}>
        <div style={{ color: t.textSub, marginBottom: 6 }}>
          <strong style={{ color: t.text }}>Query:</strong> #{payload.query_id}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <Avatar
            size={32}
            icon={isB2B ? <ShopOutlined /> : isB2C ? <UserOutlined /> : <QuestionCircleOutlined />}
            style={{ backgroundColor: isB2B ? "#1677ff" : isB2C ? "#722ed1" : "#d48806", flexShrink: 0 }}
          />
          <div>
            <div style={{ fontWeight: 600, color: t.text }}>{payload.customer_name || "—"}</div>
            <div style={{ color: t.textSub, fontSize: 11 }}>
              {isB2B ? "B2B · Garage" : isB2C ? "B2C · Retail" : "Type unknown"}
            </div>
          </div>
        </div>
        <div style={{ fontSize: 11, color: t.textSub }}>
          Submitted by{" "}
          <strong style={{ color: t.text }}>{payload.submitted_by_name || "—"}</strong>
          {approval.submitted_at && (() => {
            const d = dayjs.utc(approval.submitted_at.replace(" ", "T"));
            return ` · ${d.format("DD MMM · hh:mm A")} · ${d.from(dayjs.utc().add(4, "hour"))}`;
          })()}
        </div>
        {approval.resubmission_reason && (
          <Alert
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            style={{ marginTop: 8, fontSize: 12 }}
            message={<><strong>Resubmission:</strong> {approval.resubmission_reason}</>}
          />
        )}
      </div>

      <div style={{ fontSize: 12 }}>Required priority: <strong>{approval.required_priority ?? 0}</strong></div>
      {(payload.approval_reasons || []).map(reason => <Tag key={reason} color="orange">{{ quote_send_permission_required: "Direct quote sharing requires approval", below_recommended_gp: "Below recommended GP" }[reason] || reason}</Tag>)}
      {pricingNotice && <Alert type="warning" showIcon message={pricingNotice} />}
      {needsDedicatedCycle && <Alert type="error" showIcon message="An ordinary approval cannot authorize prices below recommended GP. Restore the recommended base prices, or reject this request so the salesperson can submit a dedicated below-GP request." />}
      {/* Visible customer delivery is independent of internal policy allocation. */}
      <div style={{
        border: "1px solid #91caff", borderRadius: 6, padding: "8px 10px", flexShrink: 0,
        background: isDark ? "rgba(24,144,255,0.12)" : "#e6f7ff",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <Checkbox checked={deliveryEnabled} onChange={e => { setDeliveryEnabled(e.target.checked); markDirty(); }}>Customer delivery</Checkbox>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <InputNumber
            value={deliveryAmount}
            min={0} disabled={!deliveryEnabled}
            size="small"
            prefix="AED"
            style={{ width: 110 }}
            onChange={v => { setDeliveryAmount(v ?? 0); markDirty(); }}
          />
          {selectedPartsForInjection.length > 1 && (
            <span style={{ fontSize: 11, color: t.textSub }}>
              ÷ {selectedPartsForInjection.length} = <strong>AED {(deliveryAmount / selectedPartsForInjection.length).toFixed(2)}/part</strong>
            </span>
          )}
        </div>
      </div>

      <div style={{ fontWeight: 600, fontSize: 12, color: t.textSub, flexShrink: 0 }}>Parts</div>

      {(payload.parts ?? []).map(p => {
        const checked = includedPartIds.has(p.query_part_id);
        const { pct = 5, mode = "excl" } = partVat[p.query_part_id] ?? {};
        const calculated = pricedById.get(Number(p.query_part_id));
      const sp = calculated.sp;
        const cp = calculated.cp ?? 0;
        const baseSp = calculated.base_sp;
        const gp = gpFromSp(cp, baseSp);
        const gpColor = gp > 0 ? "#389e0d" : "#cf1322";
        const share = checked ? deliveryPerPart : 0;
        const finalPrice = money(mode === "incl" ? (sp + share) * (1 + pct / 100) : (sp + share));
        const vatSuffix = mode === "incl" ? `(Incl. VAT ${pct}%)` : mode === "excl" ? `+ VAT ${pct}%` : "";
        const inclVatPrice = money((sp + share) * (1 + pct / 100));

        return (
          <div
            key={p.query_part_id}
            style={{
              border: `1px solid ${checked ? "#b7eb8f" : t.border}`,
              borderRadius: 6, padding: "8px 10px",
              background: checked ? (isDark ? "rgba(82,196,26,0.12)" : "#f6ffed") : undefined, flexShrink: 0,
              transition: "background 0.15s, border-color 0.15s",
            }}
          >
            <div style={{ display: "flex", alignItems: "stretch", gap: 10 }}>
              <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                <div
                  style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
                  onClick={() => togglePart(p.query_part_id)}
                >
                  <Checkbox checked={checked} onChange={() => {}} />
                  <span style={{ fontWeight: 600, fontSize: 12 }}>{p.part_name}</span>
                  <Tooltip title={Number(p.warranty_days || 0) > 0 ? `${p.warranty_days} days replacement warranty` : "No warranty"}>
                    <span style={{ color: t.textSub, fontSize: 11, cursor: "default" }}>
                      {p.condition || "New"}
                      {Number(p.warranty_days || 0) > 0 ? ` · ${p.warranty_days}d` : " · No warranty"}
                    </span>
                  </Tooltip>
                  <span onClick={e => e.stopPropagation()}>
                    <Checkbox
                      checked={partOldExchange[p.query_part_id] ?? !!p.old_exchange}
                      onChange={() => toggleOldExchange(p.query_part_id)}
                    >
                      <span style={{ fontSize: 11 }}>With Old Exchange</span>
                    </Checkbox>
                  </span>
                </div>
                <div style={{ marginTop: 6, marginLeft: 24, fontSize: 11, color: t.textSub, lineHeight: 1.7, display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }} onClick={e => e.stopPropagation()}>
                    <Tooltip title="Cost price — what the supplier quoted">
                      <span style={{ cursor: "default" }}>
                        CP: <strong style={{ color: t.text }}>AED {cp.toLocaleString()}</strong>
                      </span>
                    </Tooltip>
                    {" · "}
                    <Tooltip title="GP percentage on supplier cost, before internal allocation">
                      <span style={{ cursor: "default" }}>
                        GP: <strong style={{ color: gpColor }}>{gp.toFixed(1)}%</strong>
                      </span>
                    </Tooltip>
                    <span>Base</span>
                    <InputNumber
                      precision={2}
                      aria-label={`Base selling price for ${p.part_name}`}
                      value={baseSp}
                      min={0}
                      size="small"
                      prefix="AED"
                      style={{ width: 110, fontWeight: 600 }}
                      onChange={v => { setPartSp(prev => ({ ...prev, [p.query_part_id]: v ?? 0 })); markDirty(); }}
                    />
                    {checked && share > 0 && (
                      <Tooltip title={`Delivery share (${deliveryAmount} ÷ ${selectedPartsForInjection.length} part${selectedPartsForInjection.length > 1 ? "s" : ""})`}>
                        <span style={{ color: "#1677ff", cursor: "default" }}>
                          +{share % 1 === 0 ? share : share.toFixed(2)} del
                        </span>
                      </Tooltip>
                    )}
                  </div>
                  {checked && <div style={{ padding: 8, borderRadius: 6, background: calculated.below_recommended_gp ? (isDark ? "rgba(255,77,79,0.14)" : "#fff1f0") : t.hover, color: t.text }}>
                    {calculated.below_recommended_gp && <Tag color="red">Below recommended GP</Tag>}
                    <div>Recommended GP: {calculated.recommended_gp_percentage}% · Base: AED {money(calculated.recommended_base_selling_price).toFixed(2)}</div>
                    <div>Submitted base: AED {money(baseSp).toFixed(2)} · Shortfall: AED {money(Math.max(0, calculated.recommended_base_selling_price - baseSp)).toFixed(2)}</div>
                    <div>Internal allocation: AED {money(calculated.internal_delivery_allocation).toFixed(2)}</div>
                    <div>Customer delivery: AED {money(share).toFixed(2)}</div>
                  </div>}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }} onClick={e => e.stopPropagation()}>
                    {/* App.css's global ".ant-select { width: 100% !important; }"
                        overrides any inline width on the Select itself, so the
                        fixed width has to live on this wrapper instead — the
                        Select then resolves its 100% against this div. */}
                    <div style={{ width: 168, flexShrink: 0 }}>
                      <Select
                        value={mode}
                        size="small"
                        onChange={v => updateVat(p.query_part_id, "mode", v)}
                        options={[
                          { value: "incl", label: "Including VAT" },
                          { value: "excl", label: "Excluding VAT" },
                          { value: "none", label: "No VAT" },
                        ]}
                      />
                    </div>
                    <InputNumber
                      value={pct} min={0} max={100} size="small" disabled={mode === "none"} suffix="%"
                      style={{ width: 70 }}
                      onChange={v => updateVat(p.query_part_id, "pct", v ?? 0)}
                    />
                  </div>
                </div>
              </div>
              {/* Spans all 3 rows to the left (name row + both detail rows),
                  vertically centered — this is the number that matters most
                  in this block. */}
              <div style={{ width: 1, alignSelf: "stretch", background: t.border, flexShrink: 0 }} />
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, flexShrink: 0, padding: "0 2px" }}>
                <Tooltip title="Final customer-facing price after SP + delivery + VAT">
                  <span style={{
                    color: "#389e0d", fontWeight: 700, fontSize: 20,
                    background: isDark ? "rgba(82,196,26,0.12)" : "#f6ffed", border: "1px solid #b7eb8f",
                    borderRadius: 6, padding: "4px 12px", cursor: "default",
                    whiteSpace: "nowrap",
                  }}>
                    AED {finalPrice.toLocaleString()}
                  </span>
                </Tooltip>
                {vatSuffix && <span style={{ fontSize: 11, color: t.textSub }}>{vatSuffix}</span>}
                {mode !== "none" && (
                  <span style={{ fontSize: 10, color: t.textSub }}>Incl. VAT: AED {inclVatPrice.toLocaleString()}</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const rightCol = (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, minHeight: 0 }}>
      {manualChannel && (
        <Alert
          type="warning"
          showIcon
          message={`This customer/query uses ${communicationChannel.label} as the preferred communication channel. Approving will record that the quote was shared manually; SRM will not send an automated WATI message`}
        />
      )}
      {isCustomised && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ flexShrink: 0, fontSize: 11 }}
          message={
            <div>
              <strong>Agent customised this message</strong> — the text was manually edited before submission.
              <div style={{ marginTop: 6, display: "flex", gap: 8 }}>
                <Button
                  size="small"
                  type={keepAgentText ? "primary" : "default"}
                  onClick={() => { setKeepAgentText(true); setTextManuallyEdited(false); setMessageText(payload.message_text ?? ""); }}
                >
                  Keep agent's text
                </Button>
                <Button
                  size="small"
                  type={!keepAgentText ? "primary" : "default"}
                  icon={<ReloadOutlined />}
                  loading={qrLoading}
                  onClick={() => { setKeepAgentText(false); setTextManuallyEdited(false); }}
                >
                  Use fresh template
                </Button>
              </div>
            </div>
          }
        />
      )}

      {!isCustomised && (
        <Tabs
          activeKey={activeTab}
          onChange={v => { setActiveTab(v); setTextManuallyEdited(false); markDirty(); }}
          size="small"
          style={{ flexShrink: 0 }}
          items={[
            { key: "b2c", label: "B2C · Retail" },
            { key: "b2b", label: "B2B · Garage" },
          ]}
        />
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        {textManuallyEdited && (
          <>
            <Tag color="orange" style={{ fontSize: 11, margin: 0 }}>Manually edited</Tag>
            <Tooltip title="Discard your edits and restore auto-generated text">
              <Button
                size="small" type="text"
                style={{ fontSize: 11, color: t.textSub, padding: "0 4px" }}
                onClick={() => { setTextManuallyEdited(false); }}
              >
                Reset to template
              </Button>
            </Tooltip>
          </>
        )}
      </div>

      {qrLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 32 }}><Spin /></div>
      ) : (
        <Input.TextArea
          value={messageText}
          onChange={e => { setMessageText(e.target.value); setTextManuallyEdited(true); markDirty(); }}
          style={{
            fontSize: 12, fontFamily: "inherit", flex: 1, resize: "none", overflowY: "auto",
          }}
          placeholder="Message preview…"
        />
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <CheckCircleOutlined style={{ color: "#faad14" }} />
          {belowGpAction ? "Review below-GP quote" : "Review quote"} — #{payload.query_id}
          {isDirty && <Tag color="orange" style={{ fontSize: 10, margin: 0 }}>Modified</Tag>}
        </div>
      }
      style={{ top: 16, paddingBottom: 0 }}
      styles={{
        content: { height: "calc(100vh - 32px)", display: "flex", flexDirection: "column", padding: 0, overflow: "hidden" },
        header: { padding: "16px 24px", flexShrink: 0 },
        body: { flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column", padding: "0 24px" },
        footer: { padding: "12px 24px", flexShrink: 0 },
      }}
      footer={
        !canDecide ? <Button onClick={onClose}>Close</Button> : rejectMode ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Input.TextArea
              rows={2}
              placeholder="Reason for rejection (required)…"
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              autoFocus
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Button onClick={() => { setRejectMode(false); setRejectReason(""); }}>Cancel</Button>
              <Button
                danger type="primary"
                icon={<CloseCircleOutlined />}
                loading={rejecting}
                onClick={handleReject}
              >
                Confirm Rejection
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <Button danger icon={<CloseCircleOutlined />} onClick={() => setRejectMode(true)}>
              Reject
            </Button>
            <div style={{ display: "flex", gap: 8 }}>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={approving}
                disabled={!messageText.trim() || selectedPartsForInjection.length === 0 || fullTaskLoading || policyLoading || needsDedicatedCycle}
                onClick={handleApprove}
              >
                {manualChannel ? "Approve Quote Recording" : "Approve & Send"}{isDirty ? " (modified)" : ""}
              </Button>
            </div>
          </div>
        )
      }
      width={1080}
      destroyOnHidden
      zIndex={1060}
    >
      <div style={{ display: "grid", gridTemplateColumns: "260px 380px 1fr", gap: 16, flex: 1, minHeight: 0 }}>
        {contextCol}
        {leftCol}
        {rightCol}
      </div>
    </Modal>
  );
}
